import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { speak } from '../../components/TranslatableText';
import { isDue, reviewCard, type Card } from '../../lib/cards';
import { languageName } from '../../lib/languages';
import { useClassroom } from '../../state/Classroom';
import { useNoPaste } from '../../lib/noPaste';

// ─────────────────────────────────────────────────────────────────────────────
// Flashcards, Game Mode: Snake under the sea. A banded sea snake swims around
// eating shells, starfish and shrimp; each one brings up a card, and the
// student types the other side.
//
//   right          the snake grows; the board lights up green
//   one letter off "Oops, a typo!" (a missing accent counts as one): still right
//   more than one  wrong; the answer is shown, and the snake doesn't grow
//
// Then a 3-2-1 countdown and the snake swims on. Crash into a wall or yourself
// and it's game over: play again and keep going through the deck.
//
// The deck: due cards first, then the rest, shuffled, round and round. A DUE
// card's first answer in a game counts as its review (right = Good, wrong =
// Forgot), like a normal review; everything else is practice.
//
// It's a way to do the day's review, so a game only STARTS when cards are due.
// Once started, a round goes on until the snake crashes, practising the rest
// of the deck after the due cards are done; then it's over until more are due.
// ─────────────────────────────────────────────────────────────────────────────

/** The board: wide on computers and tablets held sideways, tall on phones held upright. */
const WIDE = { cols: 20, rows: 14 };
const TALL = { cols: 14, rows: 20 };
const boardFor = () => (typeof window !== 'undefined' && window.innerWidth < 700 && window.innerHeight > window.innerWidth ? TALL : WIDE);
type Pt = { x: number; y: number };
type Dir = 'up' | 'down' | 'left' | 'right';
type Phase = 'ready' | 'countdown' | 'playing' | 'question' | 'feedback' | 'over';
type Verdict = { kind: 'right' | 'typo' | 'wrong'; answer: string; note?: string };
const FOODS = ['shell', 'star', 'shrimp', 'pearl'] as const;
type Food = Pt & { kind: (typeof FOODS)[number]; card: Card };
const STEP: Record<Dir, Pt> = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } };
const OPPOSITE: Record<Dir, Dir> = { up: 'down', down: 'up', left: 'right', right: 'left' };

// ── Checking an answer ──────────────────────────────────────────────────────
const clean = (s: string) => s.toLowerCase().replace(/[.,!?¿¡"“”'’()[\]:;]/g, ' ').replace(/\s+/g, ' ').trim();
const noAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
/** Little words people leave off: "to run" = "run", "the house" = "house", "la casa" = "casa". */
const ARTICLES = /^(to|the|a|an|el|la|los|las|un|una|le|les|l|der|die|das|ein|eine|il|lo|gli|o|os|as|um|uma)\s+/;
function variants(answer: string): string[] {
  const parts = answer.split(/[,;/]|\bor\b|\bo\b/).map(clean).filter(Boolean);
  const all = new Set<string>([clean(answer), ...parts]);
  for (const p of [...all]) all.add(p.replace(ARTICLES, ''));
  return [...all].filter(Boolean);
}
/** Edits between two words (a swap of neighbouring letters counts as one); 2 means "2 or more". */
function distance(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 1) return 2;
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) {
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    // Two neighbouring letters swapped ("huose") is one slip, not two.
    if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
  }
  return d[a.length][b.length];
}
export function checkAnswer(typed: string, answer: string): Verdict {
  const t = clean(typed), tBare = t.replace(ARTICLES, '');
  if (!t) return { kind: 'wrong', answer };
  const vs = variants(answer);
  if (vs.some(v => v === t || v === tBare)) return { kind: 'right', answer };
  if (vs.some(v => noAccents(v) === noAccents(t) || noAccents(v) === noAccents(tBare))) return { kind: 'typo', answer, note: 'Watch the accents!' };
  if (vs.some(v => distance(noAccents(v), noAccents(t)) <= 1 || distance(noAccents(v), noAccents(tBare)) <= 1)) return { kind: 'typo', answer };
  return { kind: 'wrong', answer };
}

// ── Sounds (Web Audio, no files) ────────────────────────────────────────────
let actx: AudioContext | null = null;
function tone(notes: [number, number, number][], type: OscillatorType = 'sine', vol = 0.15) {
  try {
    actx ??= new AudioContext();
    if (actx.state === 'suspended') void actx.resume();
    const t0 = actx.currentTime;
    for (const [f, at, dur] of notes) {
      const o = actx.createOscillator(), g = actx.createGain();
      o.type = type; o.frequency.setValueAtTime(f, t0 + at);
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(vol, t0 + at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      o.connect(g).connect(actx.destination); o.start(t0 + at); o.stop(t0 + at + dur + 0.05);
    }
  } catch { /* no sound: fine */ }
}
const sfx = {
  eat: () => tone([[520, 0, 0.08], [780, 0.06, 0.1]], 'triangle'),
  right: () => tone([[659, 0, 0.12], [784, 0.1, 0.12], [1047, 0.2, 0.25]], 'triangle'),
  typo: () => tone([[587, 0, 0.12], [740, 0.12, 0.2]], 'triangle'),
  wrong: () => tone([[220, 0, 0.18], [165, 0.16, 0.3]], 'square', 0.08),
  beep: (hi: boolean) => tone([[hi ? 880 : 440, 0, 0.12]], 'sine', 0.12),
  crash: () => tone([[300, 0, 0.1], [200, 0.08, 0.15], [110, 0.2, 0.4]], 'sawtooth', 0.08),
};

// ── Drawing ─────────────────────────────────────────────────────────────────
const INK = '#1e2633';
function drawBoard(g: CanvasRenderingContext2D, W: number, H: number, cell: number, t: number, flash: number) {
  const bg = g.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#2fa3d6'); bg.addColorStop(0.6, '#1667a3'); bg.addColorStop(1, '#0d3f6e');
  g.fillStyle = bg; g.fillRect(0, 0, W, H);
  // Light rays.
  g.save(); g.globalAlpha = 0.08; g.fillStyle = '#fff';
  for (let i = 0; i < 4; i++) { const x = (i + 0.3) * W / 4 + Math.sin(t * 0.3 + i) * 20; g.beginPath(); g.moveTo(x - 20, 0); g.lineTo(x + 20, 0); g.lineTo(x + 90, H); g.lineTo(x + 30, H); g.closePath(); g.fill(); }
  g.restore();
  // Faint grid, like ripples on sand.
  g.strokeStyle = 'rgba(255,255,255,0.05)'; g.lineWidth = 1;
  for (let x = 0; x <= W / cell + 0.01; x++) { g.beginPath(); g.moveTo(x * cell, 0); g.lineTo(x * cell, H); g.stroke(); }
  for (let y = 0; y <= H / cell + 0.01; y++) { g.beginPath(); g.moveTo(0, y * cell); g.lineTo(W, y * cell); g.stroke(); }
  // Seaweed along the bottom and bubbles drifting up.
  for (let i = 0; i < 9; i++) {
    const x = (i + 0.5) * W / 9, h = cell * (1.2 + (i % 3) * 0.5), sway = Math.sin(t * 1.2 + i) * 6;
    g.strokeStyle = i % 2 ? 'rgba(63,155,95,0.55)' : 'rgba(47,129,80,0.55)'; g.lineWidth = 4;
    g.beginPath(); g.moveTo(x, H); g.quadraticCurveTo(x + sway * 0.5, H - h / 2, x + sway, H - h); g.stroke();
  }
  g.fillStyle = 'rgba(255,255,255,0.35)';
  for (let i = 0; i < 12; i++) {
    const p = ((t * 0.08 + i * 0.137) % 1);
    g.beginPath(); g.arc(((i * 97) % W) + Math.sin(t + i) * 6, H - p * H, 2 + (i % 3), 0, Math.PI * 2); g.fill();
  }
  if (flash > 0) { g.fillStyle = `rgba(80,230,120,${0.35 * flash})`; g.fillRect(0, 0, W, H); }
}

export function drawFood(g: CanvasRenderingContext2D, f: Food, cell: number, t: number) {
  const cx = (f.x + 0.5) * cell, cy = (f.y + 0.5) * cell + Math.sin(t * 3 + f.x) * 2, r = cell * 0.36;
  const halo = g.createRadialGradient(cx, cy, 2, cx, cy, cell * 0.9);
  halo.addColorStop(0, 'rgba(255,240,170,0.55)'); halo.addColorStop(1, 'rgba(255,240,170,0)');
  g.fillStyle = halo; g.beginPath(); g.arc(cx, cy, cell * 0.9, 0, Math.PI * 2); g.fill();
  g.save(); g.translate(cx, cy); g.lineJoin = 'round'; g.strokeStyle = INK; g.lineWidth = 2;
  if (f.kind === 'star') {
    g.rotate(Math.sin(t * 2) * 0.2); g.fillStyle = '#ff9a3d'; g.beginPath();
    for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r; g.lineTo(Math.cos(a) * rr, Math.sin(a) * rr); }
    g.closePath(); g.fill(); g.stroke();
  } else if (f.kind === 'shell') {
    // A scallop: a ribbed fan on a little hinge.
    g.fillStyle = '#ffc7d6';
    g.beginPath(); g.moveTo(-r * 0.2, r * 0.75); g.lineTo(-r, -r * 0.05);
    g.quadraticCurveTo(-r * 0.95, -r * 0.95, 0, -r); g.quadraticCurveTo(r * 0.95, -r * 0.95, r, -r * 0.05);
    g.lineTo(r * 0.2, r * 0.75); g.closePath(); g.fill(); g.stroke();
    g.strokeStyle = 'rgba(30,38,51,0.5)'; g.lineWidth = 1.2;
    for (const k of [-0.7, -0.35, 0, 0.35, 0.7]) { g.beginPath(); g.moveTo(0, r * 0.72); g.lineTo(k * r, -r * 0.82 + Math.abs(k) * r * 0.3); g.stroke(); }
    g.strokeStyle = INK; g.lineWidth = 2; g.fillStyle = '#ffa8bd';
    g.beginPath(); g.roundRect(-r * 0.32, r * 0.62, r * 0.64, r * 0.3, 3); g.fill(); g.stroke();
  } else if (f.kind === 'shrimp') {
    // A curled shrimp: a segmented body, a tail fan, and long feelers.
    g.lineCap = 'round';
    g.strokeStyle = INK; g.lineWidth = r * 0.62 + 4;
    g.beginPath(); g.arc(0, 0, r * 0.62, Math.PI * 0.15, Math.PI * 1.55); g.stroke();
    g.strokeStyle = '#ff8a6b'; g.lineWidth = r * 0.62;
    g.beginPath(); g.arc(0, 0, r * 0.62, Math.PI * 0.15, Math.PI * 1.55); g.stroke();
    g.strokeStyle = 'rgba(30,38,51,0.45)'; g.lineWidth = 1.2;
    for (let i = 1; i < 5; i++) { const a = Math.PI * (0.15 + i * 0.28); g.beginPath(); g.moveTo(Math.cos(a) * r * 0.32, Math.sin(a) * r * 0.32); g.lineTo(Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.92); g.stroke(); }
    const ta = Math.PI * 1.55, tx = Math.cos(ta) * r * 0.62, ty = Math.sin(ta) * r * 0.62;
    g.fillStyle = '#ff8a6b'; g.strokeStyle = INK; g.lineWidth = 1.8;
    g.beginPath(); g.moveTo(tx, ty); g.lineTo(tx + r * 0.45, ty - r * 0.35); g.lineTo(tx + r * 0.5, ty + r * 0.1); g.closePath(); g.fill(); g.stroke();
    const ha = Math.PI * 0.15, hx = Math.cos(ha) * r * 0.62, hy = Math.sin(ha) * r * 0.62;
    g.beginPath(); g.moveTo(hx, hy); g.quadraticCurveTo(hx + r * 0.8, hy - r * 0.2, hx + r * 0.9, hy - r * 0.9); g.stroke();
    g.fillStyle = INK; g.beginPath(); g.arc(hx - 1, hy - 3, 2.2, 0, Math.PI * 2); g.fill();
  } else {
    const pg = g.createRadialGradient(-r * 0.3, -r * 0.3, 1, 0, 0, r);
    pg.addColorStop(0, '#ffffff'); pg.addColorStop(1, '#c9d8ff');
    g.fillStyle = pg; g.beginPath(); g.arc(0, 0, r * 0.75, 0, Math.PI * 2); g.fill(); g.stroke();
  }
  g.restore();
}

/**
 * A banded sea snake, drawn as one smooth body that glides between squares
 * (like Google's Snake): the head slides from the square it left toward the
 * one it's entering, the tail slides off the square it's leaving, and corners
 * stay round. `alpha` is how far through the current step it is (0 to 1).
 */
function drawSnake(g: CanvasRenderingContext2D, st: { body: Pt[]; prevTail: Pt; dir: Dir; prevDir: Dir; steps: number }, alpha: number, cell: number, t: number, glow: number) {
  const { body } = st;
  const c = (p: Pt) => ({ x: (p.x + 0.5) * cell, y: (p.y + 0.5) * cell });
  const lerp = (a: { x: number; y: number }, b: { x: number; y: number }, k: number) => ({ x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k });
  const w = cell * 0.74;
  // The body's centre line: the head partway into its square, every square's centre, the tail partway out of the last.
  const head = body.length > 1 ? lerp(c(body[1]), c(body[0]), alpha) : c(body[0]);
  const tailEnd = lerp(c(st.prevTail), c(body[body.length - 1]), alpha);
  const pts = [head, ...body.slice(1).map(c), tailEnd];
  const trace = () => { g.beginPath(); g.moveTo(pts[0].x, pts[0].y); for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y); };

  g.save();
  g.lineCap = 'round'; g.lineJoin = 'round';
  if (glow > 0) { g.shadowColor = `rgba(90,255,140,${glow})`; g.shadowBlur = 18 * glow; }
  // Tail paddle, angled off the end.
  const tp = pts[pts.length - 2], ang = Math.atan2(tailEnd.y - tp.y, tailEnd.x - tp.x) || Math.atan2(-STEP[st.dir].y, -STEP[st.dir].x);
  g.save(); g.translate(tailEnd.x, tailEnd.y); g.rotate(ang + Math.sin(t * 7) * 0.3);
  g.fillStyle = '#2b4c7e'; g.strokeStyle = INK; g.lineWidth = 2;
  g.beginPath(); g.moveTo(-cell * 0.05, 0); g.quadraticCurveTo(cell * 0.55, -cell * 0.45, cell * 0.68, 0); g.quadraticCurveTo(cell * 0.55, cell * 0.45, -cell * 0.05, 0); g.fill(); g.stroke();
  g.restore();
  // Outline, light body, then dark bands that travel with the body (they're measured from the tail end).
  trace(); g.strokeStyle = INK; g.lineWidth = w + 4; g.stroke();
  g.shadowBlur = 0;
  trace(); g.strokeStyle = '#8fd3ff'; g.lineWidth = w; g.stroke();
  const moved = (st.steps + alpha) * cell;
  g.setLineDash([cell * 0.34, cell * 0.66]); g.lineDashOffset = -moved; g.lineCap = 'butt';
  trace(); g.strokeStyle = '#24324f'; g.lineWidth = w; g.stroke();
  g.setLineDash([]); g.lineCap = 'round';
  // A pale belly stripe down the middle.
  trace(); g.strokeStyle = 'rgba(255,255,255,0.18)'; g.lineWidth = w * 0.22; g.stroke();
  g.restore();

  // The head, turning smoothly through corners.
  const a1 = Math.atan2(STEP[st.dir].y, STEP[st.dir].x), a0 = Math.atan2(STEP[st.prevDir].y, STEP[st.prevDir].x);
  let da = a1 - a0; while (da > Math.PI) da -= Math.PI * 2; while (da < -Math.PI) da += Math.PI * 2;
  const k = Math.min(1, alpha * 1.6), angH = a0 + da * (k * k * (3 - 2 * k));
  g.save(); g.translate(head.x, head.y); g.rotate(angH);
  g.fillStyle = '#8fd3ff'; g.strokeStyle = INK; g.lineWidth = 2;
  g.beginPath(); g.ellipse(cell * 0.1, 0, cell * 0.48, cell * 0.4, 0, 0, Math.PI * 2); g.fill(); g.stroke();
  for (const ey of [-0.18, 0.18]) {
    g.fillStyle = '#fff'; g.beginPath(); g.arc(cell * 0.24, ey * cell, cell * 0.11, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = INK; g.beginPath(); g.arc(cell * 0.27, ey * cell, cell * 0.05, 0, Math.PI * 2); g.fill();
  }
  if (Math.sin(t * 6) > 0.6) { g.strokeStyle = '#ff5a8a'; g.lineWidth = 2; g.beginPath(); g.moveTo(cell * 0.56, 0); g.lineTo(cell * 0.76, 0); g.lineTo(cell * 0.83, -4); g.moveTo(cell * 0.76, 0); g.lineTo(cell * 0.83, 4); g.stroke(); }
  g.restore();
}

// ── The game ────────────────────────────────────────────────────────────────
export default function SnakeGame({ onExit, back = 'Flashcards' }: { onExit: () => void; back?: string }) {
  const { cards, setCards, active } = useClassroom();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const goRef = useRef<HTMLButtonElement>(null);
  const timer = useRef(0);
  useEffect(() => () => window.clearInterval(timer.current), []);
  const [phase, setPhase] = useState<Phase>('ready');
  const [count, setCount] = useState(3);
  const [side, setSide] = useState<'meaning' | 'word'>('meaning');
  const [typed, setTyped] = useState('');
  const noPaste = useNoPaste();
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => { try { return Number(localStorage.getItem('snake.best')) || 0; } catch { return 0; } });
  const [tally, setTally] = useState({ right: 0, wrong: 0 });
  const [question, setQuestion] = useState<Card | null>(null);
  const lang = active?.language ?? cards[0]?.source_lang ?? 'es';

  // The game's moving parts live in refs (the loop reads them every frame).
  const g = useRef({ body: [] as Pt[], prevTail: { x: 0, y: 0 } as Pt, dir: 'right' as Dir, prevDir: 'right' as Dir, steps: 0, next: 'right' as Dir, food: null as Food | null, acc: 0, speed: 0.16, flash: 0, glow: 0 });
  // Dev builds only: the game's state, for testing it by script (snake-demo.html).
  if (import.meta.env.DEV) (window as unknown as { __snake?: unknown }).__snake = g.current;
  const [boardSize, setBoardSize] = useState(boardFor);
  const board = useRef(boardSize); board.current = boardSize;
  const deck = useRef<Card[]>([]);
  const graded = useRef(new Set<string>());
  const phaseRef = useRef(phase); phaseRef.current = phase;

  // Due cards first, then the rest, shuffled; refilled when it runs out.
  const nextCard = useCallback((): Card | null => {
    if (!deck.current.length) {
      const due = cards.filter(c => isDue(c) && !graded.current.has(c.id)).sort(() => Math.random() - 0.5);
      const rest = cards.filter(c => !due.includes(c)).sort(() => Math.random() - 0.5);
      deck.current = [...due, ...rest];
    }
    return deck.current.shift() ?? null;
  }, [cards]);

  const placeFood = useCallback(() => {
    const st = g.current;
    const card = nextCard();
    if (!card) { st.food = null; return; }
    for (let tries = 0; tries < 400; tries++) {
      const p = { x: Math.floor(Math.random() * board.current.cols), y: Math.floor(Math.random() * board.current.rows) };
      if (st.body.some(b => b.x === p.x && b.y === p.y)) continue;
      st.food = { ...p, kind: FOODS[Math.floor(Math.random() * FOODS.length)], card };
      return;
    }
  }, [nextCard]);

  const reset = useCallback(() => {
    const st = g.current;
    // A new game fits the screen as it's held now.
    const b = boardFor(); board.current = b; setBoardSize(b);
    const my = Math.floor(b.rows / 2);
    st.body = [{ x: 5, y: my }, { x: 4, y: my }, { x: 3, y: my }];
    st.prevTail = { x: 2, y: my }; st.steps = 0;
    st.dir = st.next = st.prevDir = 'right'; st.acc = 0; st.speed = 0.16; st.flash = 0; st.glow = 0;
    setScore(0); setTally({ right: 0, wrong: 0 });
    placeFood();
  }, [placeFood]);

  const countdown = useCallback(() => {
    setPhase('countdown'); setCount(3); sfx.beep(false);
    let n = 3;
    window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      n--;
      if (n > 0) { setCount(n); sfx.beep(false); }
      else { window.clearInterval(timer.current); sfx.beep(true); setPhase('playing'); }
    }, 750);
  }, []);

  const start = () => { reset(); countdown(); };

  // Keys: arrows / WASD steer; Enter goes on after an answer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, Dir> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', w: 'up', s: 'down', a: 'left', d: 'right', W: 'up', S: 'down', A: 'left', D: 'right' };
      const d = map[e.key];
      if (d && (phaseRef.current === 'playing' || phaseRef.current === 'countdown')) {
        e.preventDefault();
        const st = g.current;
        if (d !== OPPOSITE[st.dir]) st.next = d;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Swipes on phones and tablets.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    let sx = 0, sy = 0;
    const down = (e: PointerEvent) => { sx = e.clientX; sy = e.clientY; };
    const up = (e: PointerEvent) => {
      const dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
      const d: Dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
      if (d !== OPPOSITE[g.current.dir]) g.current.next = d;
    };
    c.addEventListener('pointerdown', down); c.addEventListener('pointerup', up);
    return () => { c.removeEventListener('pointerdown', down); c.removeEventListener('pointerup', up); };
  }, []);

  // The loop: move on a timer, draw every frame.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    let raf = 0, last = performance.now(), alive = true;
    const frame = (now: number) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const st = g.current, t = now / 1000;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const { cols, rows } = board.current;
      const W = c.clientWidth, cell = W / cols, H = cell * rows;
      if (c.width !== Math.round(W * dpr) || c.height !== Math.round(H * dpr)) { c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      st.flash = Math.max(0, st.flash - dt * 1.5);
      st.glow = Math.max(0, st.glow - dt);

      if (phaseRef.current === 'playing' && st.body.length) {
        st.acc += dt;
        while (st.acc >= st.speed && phaseRef.current === 'playing') {
          // The head has just arrived in its square. Food there? Eat it now, holding still right on it.
          const h = st.body[0];
          if (st.food && h.x === st.food.x && h.y === st.food.y) {
            st.acc = st.speed;
            sfx.eat();
            setQuestion(st.food.card);
            setTyped(''); setVerdict(null);
            setPhase('question');
            break;
          }
          st.acc -= st.speed;
          st.prevDir = st.dir;
          st.dir = st.next;
          const s = STEP[st.dir], nh = { x: h.x + s.x, y: h.y + s.y };
          const hitsSelf = st.body.slice(0, -1).some(b => b.x === nh.x && b.y === nh.y);
          if (nh.x < 0 || nh.y < 0 || nh.x >= board.current.cols || nh.y >= board.current.rows || hitsSelf) {
            st.acc = st.speed;   // stop right at the edge of the square
            st.dir = st.prevDir;
            sfx.crash();
            setPhase('over');
            break;
          }
          st.body.unshift(nh);
          st.prevTail = st.body.pop()!;
          st.steps++;
        }
      }
      drawBoard(ctx, W, H, cell, t, st.flash);
      if (st.food && phaseRef.current !== 'question') drawFood(ctx, st.food, cell, t);
      if (st.body.length) drawSnake(ctx, st, Math.min(1, st.acc / st.speed), cell, t, st.glow);
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => { if (phase === 'question') window.setTimeout(() => inputRef.current?.focus(), 30); }, [phase]);

  // Best score, kept in this browser.
  useEffect(() => {
    if (phase !== 'over' || score <= best) return;
    setBest(score);
    try { localStorage.setItem('snake.best', String(score)); } catch { /* fine */ }
  }, [phase, score, best]);

  const prompt = question ? (side === 'meaning' ? question.term : question.translation) : '';
  const answer = question ? (side === 'meaning' ? question.translation : question.term) : '';
  const promptLang = question ? (side === 'meaning' ? question.source_lang : question.target_lang) : lang;

  const submit = async () => {
    if (!question || verdict) return;
    const v = checkAnswer(typed, answer);
    setVerdict(v);
    setPhase('feedback');
    const st = g.current;
    if (v.kind === 'wrong') { sfx.wrong(); setTally(x => ({ ...x, wrong: x.wrong + 1 })); }
    else {
      if (v.kind === 'right') sfx.right(); else sfx.typo();
      st.flash = 1; st.glow = 1.2;
      // Right answers grow the snake (and speed it up a little).
      const tail = st.body[st.body.length - 1];
      st.body.push({ ...tail });
      st.prevTail = { ...tail };
      st.speed = Math.max(0.085, st.speed - 0.004);
      setScore(s => s + (v.kind === 'right' ? 10 : 7));
      setTally(x => ({ ...x, right: x.right + 1 }));
    }
    // A due card's first answer counts as its review.
    if (isDue(question) && !graded.current.has(question.id)) {
      graded.current.add(question.id);
      try {
        const updated = await reviewCard(question, v.kind === 'wrong' ? 'forgot' : 'good');
        setCards(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      } catch { /* the game goes on; it'll be due again */ }
    }
  };

  const carryOn = () => {
    setQuestion(null); setVerdict(null);
    placeFood();
    countdown();
  };

  // After an answer, the button takes the focus, so Enter swims on.
  useEffect(() => { if (phase === 'feedback') window.setTimeout(() => goRef.current?.focus(), 30); }, [phase]);

  const dueLeft = useMemo(() => cards.filter(c => isDue(c) && !graded.current.has(c.id)).length, [cards, tally]);

  return (
    <div className="narrow wide snake-page">
      <div className="row" style={{ marginBottom: 10 }}>
        <button className="small" onClick={onExit}>← {back}</button>
        <span className="spacer" />
        <span className="small muted">🐚 {score} pts · ✅ {tally.right} · ❌ {tally.wrong}{best ? ` · Best ${best}` : ''}{dueLeft ? ` · ${dueLeft} due` : ''}</span>
      </div>

      <div className={`snake-stage${phase === 'feedback' && verdict?.kind !== 'wrong' ? ' good' : ''}${phase === 'feedback' && verdict?.kind === 'wrong' ? ' bad' : ''}`}>
        <canvas ref={canvasRef} className="snake-canvas" aria-label="Sea snake game. Arrow keys, WASD or swipe to steer."
          style={{ aspectRatio: `${boardSize.cols} / ${boardSize.rows}`, maxWidth: `calc((100dvh - 250px) * ${boardSize.cols / boardSize.rows})` }} />

        {phase === 'ready' && (
          <div className="snake-overlay">
            <div className="snake-card">
              <h2>🐍 Sea Snake</h2>
              <p className="muted" style={{ margin: 0 }}>Swim with the arrow keys (or WASD, or swipe). Eat shells, starfish and shrimp: each one is a card. Type the other side to grow!</p>
              <div className="chips" role="radiogroup" aria-label="What to type">
                <button type="button" className={`chip${side === 'meaning' ? ' on' : ''}`} aria-pressed={side === 'meaning'} onClick={() => setSide('meaning')}>Type the meaning</button>
                <button type="button" className={`chip${side === 'word' ? ' on' : ''}`} aria-pressed={side === 'word'} onClick={() => setSide('word')}>Type the word in {languageName(lang) || 'the language'}</button>
              </div>
              {dueLeft === 0 && <p className="small" style={{ margin: 0 }}>🎉 No cards are due right now. Game Mode opens again when you have cards to review.</p>}
              <button className="primary big" disabled={dueLeft === 0} onClick={start}>{dueLeft ? `Play (${dueLeft} due)` : 'Nothing due'}</button>
            </div>
          </div>
        )}

        {phase === 'countdown' && <div className="snake-overlay clear"><div className="snake-count" key={count}>{count}</div></div>}

        {(phase === 'question' || phase === 'feedback') && question && (
          <div className="snake-overlay">
            <form className={`snake-card question ${verdict?.kind ?? ''}`} onSubmit={e => { e.preventDefault(); if (phase === 'feedback') carryOn(); else void submit(); }}>
              <div className="small muted">{side === 'meaning' ? 'What does this mean?' : `How do you say this in ${languageName(lang) || 'the language'}?`}</div>
              <div className="snake-prompt" dir="auto">
                {prompt}
                <button type="button" className="icon" aria-label="Hear it" onClick={() => speak(prompt, promptLang)}>🔊</button>
              </div>
              <input ref={inputRef} value={typed} onChange={e => setTyped(e.target.value)} disabled={phase === 'feedback'} dir="auto"
                autoCapitalize="off" autoCorrect="off" spellCheck={false} placeholder="Type your answer…" aria-label="Your answer" {...noPaste.props} />
              {noPaste.warned && <div className="no-paste" role="status">✋ No pasting: type it yourself!</div>}
              {verdict && (
                <div className={`snake-verdict ${verdict.kind}`} role="status">
                  {verdict.kind === 'right' && <strong>✅ Correct!</strong>}
                  {verdict.kind === 'typo' && <><strong>😅 Oops, a typo!</strong> {verdict.note ?? 'So close.'} It’s <em dir="auto">{verdict.answer}</em>. That still counts!</>}
                  {verdict.kind === 'wrong' && <><strong>❌ Not quite.</strong> It’s <em dir="auto">{verdict.answer}</em>.</>}
                </div>
              )}
              <button ref={goRef} className="primary" type="submit">{phase === 'feedback' ? 'Keep swimming ⏎' : 'Check'}</button>
            </form>
          </div>
        )}

        {phase === 'over' && (
          <div className="snake-overlay">
            <div className="snake-card">
              <h2>💥 Bonk!</h2>
              <p style={{ margin: 0 }}><strong>{score}</strong> points · {tally.right} right · {tally.wrong} wrong{score > 0 && score >= best ? ' · 🏆 New best!' : ''}</p>
              {dueLeft === 0 && <p className="small" style={{ margin: 0 }}>🎉 You’ve reviewed every card that was due. Come back when more are due to play again!</p>}
              <div className="row" style={{ justifyContent: 'center' }}>
                <button onClick={onExit}>Done</button>
                {dueLeft > 0 && <button className="primary" onClick={start}>Play again ({dueLeft} due)</button>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="snake-pad" aria-hidden={phase !== 'playing'}>
        {(['up', 'left', 'down', 'right'] as Dir[]).map(d => (
          <button key={d} type="button" className={`pad-${d}`} aria-label={d}
            onPointerDown={e => { e.preventDefault(); if (d !== OPPOSITE[g.current.dir]) g.current.next = d; }}>{({ up: '▲', down: '▼', left: '◀', right: '▶' } as const)[d]}</button>
        ))}
      </div>
    </div>
  );
}
